import { createFileRoute } from "@tanstack/react-router";
import { useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import { DatesProvider, useDates } from "../../../../dates/DatesContext";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { ORG_SUMMARY_REPORT_BREADCRUMB_SPEC, REPORT_LINKS } from "../../../../utils/dashboardLinks";
import { useGetV4ProductReportByDate } from "../../../../modules/perf_reports/api/product/v4ProductReportByDate";
import { useQuery } from "@tanstack/react-query";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import MetricsCard from "../../../../components/custom/merchandise/MetricsCard";
import { useAcosOrRoas } from "../../../../utils/acosOrRoas";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { idsToVisibilityState, selectionStateToSelectedIds } from "../../../../components/table/utils";
import { useGetV4ProductReportTotals } from "../../../../modules/perf_reports/api/product/v4ProductReportTotals";
import { useStandardEntityMetrics } from "../../../../modules/merchandising/components/tables/metricUtils";
import ProductReportTable from "../../../../modules/perf_reports/product_report/ProductReportTable";
import { useProductReportColumns } from "../../../../modules/perf_reports/product_report/useProductReportColumns";
import useQueryParams from "../../../../hooks/useQueryParams";
import {
  productReportGroupingSchema,
  type ProductReportGrouping,
} from "../../../../zodios/common/productReportGroupingSchema";

const ProductReportBody = () => {
  const { t } = useTranslation(["perf_reports"]);
  const [grouping, setGrouping] = useQueryParams("dims", "product_id");
  const parsedGrouping = productReportGroupingSchema.safeParse(grouping);
  const safeGrouping: ProductReportGrouping = parsedGrouping.success ? parsedGrouping.data : "product_id";

  const acosOrRoas = useAcosOrRoas();
  const { start, end, group } = useDates();
  const longDate = useDateFormatter("long");

  const standardMetricIds = useStandardEntityMetrics("product");

  const columns = useProductReportColumns(undefined, safeGrouping);

  // Below is to hide additional metrics by default without having to define each one
  const defaultSelectedColumns = [
    "product_title",
    "price",
    "units",
    "sales",
    "total_acos",
    "page_views",
    "ad_sales",
    "ad_spend",
    "acos",
    "conversion_rate",
    "total_conv_rate",
    "orders",
    "ctr",
    "clicks",
    "impressions",
  ];
  const defaultHiddenCols = idsToVisibilityState(
    defaultSelectedColumns,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id as string),
  );

  const initialStateConfig = {
    columnVisibility: {
      // Hidden to support filtering
      tags: false,
      variations: false,
      category_id: false,
      sales_state: false,
      inventory_state: false,
      inventory_status: false,
      has_ads: false,
      pricing_plan_status: false,
      ad_cnt: false,
      ...defaultHiddenCols,
    },
    columnOrder: defaultSelectedColumns,
    columnPinning: {
      start: ["product_title"],
      end: [],
    },
    sorting: [{ id: "sales", desc: true }],
  };

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `productReport-${safeGrouping}`,
    initialState: initialStateConfig,
  });

  const debounceSearch = useDebounce(state.globalFilter, 500);
  const selectedIds = selectionStateToSelectedIds(state.rowSelection);
  const selectedIdsFilter =
    selectedIds.length ? [{ id: "product_id", value: { op: "anyof", value: selectedIds } }] : [];

  // Fetch product report data grouped by date to populate chart
  const byDateOptionsGetter = useGetV4ProductReportByDate();
  const productsByDateReportQuery = useQuery(
    byDateOptionsGetter({
      filters: [...state.columnFilters, ...selectedIdsFilter],
      search: debounceSearch,
    }),
  );
  const totalOptionsGetter = useGetV4ProductReportTotals();
  const productTotalsReportQuery = useQuery(
    totalOptionsGetter({
      filters: [...state.columnFilters, ...selectedIdsFilter],
      search: debounceSearch,
    }),
  );

  const metrics = useMemo(() => {
    return standardMetricIds.map((metricId) => getMetricSpec(metricId));
  }, [standardMetricIds]);

  const timeLineData = useMemo(() => {
    return productsByDateReportQuery.data?.data ?? [];
  }, [productsByDateReportQuery.data?.data]);

  return (
    <PageContainer withBreadcrumb>
      <MetricsCard
        id={"productReport"}
        title={t(($) => $.perf_reports["Performance Overview"])}
        totals={{
          data: productTotalsReportQuery.data?.data,
          loading:
            productTotalsReportQuery.isFetching &&
            (!productTotalsReportQuery.data || productTotalsReportQuery.isPlaceholderData),
        }}
        timeline={{
          data: timeLineData,
          loading:
            productsByDateReportQuery.isFetching &&
            (!productsByDateReportQuery.data || productsByDateReportQuery.isPlaceholderData),
        }}
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["ad_sales", "ad_spend", acosOrRoas("acos", "roas")]}
        group={group}
        showAnnotations={true}
        byline={
          <>
            {longDate.format(start.toDate())}
            {" - "}
            {longDate.format(end.toDate())}
          </>
        }
        context={"category"}
      >
        <ProductReportTable
          state={state}
          initialState={initialState}
          setters={setters}
          grouping={safeGrouping}
          onGroupingChange={setGrouping}
          columns={columns}
        />
      </MetricsCard>
    </PageContainer>
  );
};

function ProductReportV2() {
  const { t } = useTranslation(["common"]);
  const hasOrgSummaryreportModule = useModuleEnabled("ORG_SUMMARY_REPORT");
  const breadCrumbItems = [
    {
      name: t(($) => $.common.sidebar["Product Report"]),
      href: "/user/report/product",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: t(($) => $.common.sidebar["Reports"]),
    },
  ];

  if (
    hasOrgSummaryreportModule &&
    !breadCrumbItems[0]?.dropdownLinkOptions.find((o) => o.name === ORG_SUMMARY_REPORT_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0]?.dropdownLinkOptions.unshift(ORG_SUMMARY_REPORT_BREADCRUMB_SPEC);
  }
  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} />
      <ProductReportBody />
    </DatesProvider>
  );
}

export const Route = createFileRoute("/_authenticated/user/report/product")({
  component: ProductReportV2,
  staticData: { application: "Reports" },
});
